import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Table, PageHeader, Modal, FormField, ConfirmDialog } from '../../../components/UI';
import toast from 'react-hot-toast';

const emptyEmp = { emp_name:'', emp_code:'', dept_id:'', designation_id:'', login_id:'', password:'', role_id:'', email:'', mobile:'', is_active:1 };

export default function EmployeeMaster() {
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/masters/employees'),
      api.get('/masters/departments'),
      api.get('/masters/designations'),
      api.get('/rbac/roles'),
    ]).then(([e,d,des,r]) => {
      setItems(e.employees||[]); setDepts(d.departments||[]); setDesigs(des.designations||[]); setRoles(r.roles||[]);
    }).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);

  const openAdd = () => { setForm(emptyEmp); setEditing(null); setModal(true); };
  const openEdit = (item) => { setForm({...item, password:''}); setEditing(item); setModal(true); };
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api.put(`/masters/employees/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/masters/employees', form); toast.success('Created'); }
      setModal(false); load();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await api.delete(`/masters/employees/${deleteModal.id}`);
    toast.success('Employee deactivated'); setDeleteModal(null); load();
  };

  const filteredDesigs = form.dept_id ? desigs.filter(d=>d.dept_id==form.dept_id) : desigs;

  const columns = [
    {key:'emp_code',label:'Code'},
    {key:'emp_name',label:'Name'},
    {key:'login_id',label:'Login ID'},
    {key:'dept_name',label:'Department'},
    {key:'designation_name',label:'Designation'},
    {key:'role_name',label:'Role'},
    {key:'mobile',label:'Mobile'},
    {key:'is_active',label:'Status',render:r=><span className={r.is_active?'text-green-600 font-medium':'text-red-500 font-medium'}>{r.is_active?'Active':'Inactive'}</span>},
    {key:'actions',label:'Actions',render:r=>(
      <div className="flex gap-2">
        <button onClick={()=>openEdit(r)} className="text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 px-2 py-1 rounded">✏️</button>
        <button onClick={()=>setDeleteModal(r)} className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded">🗑️</button>
      </div>
    )}
  ];

  return (
    <div>
      <PageHeader title="Employee Master" subtitle="Manage department officers and admin staff"
        actions={<button className="btn-primary" onClick={openAdd}>+ Add Employee</button>} />
      <div className="card overflow-hidden p-0"><Table columns={columns} data={items} loading={loading} /></div>

      <Modal isOpen={modal} onClose={()=>setModal(false)} title={editing?'Edit Employee':'Add Employee'} size="xl">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Employee Name" required><input className="form-input" value={form.emp_name} onChange={e=>f('emp_name',e.target.value)} required /></FormField>
            <FormField label="Employee Code"><input className="form-input" value={form.emp_code} onChange={e=>f('emp_code',e.target.value)} /></FormField>
            <FormField label="Department">
              <select className="form-select" value={form.dept_id} onChange={e=>{f('dept_id',e.target.value);f('designation_id','');}}>
                <option value="">Select Department</option>
                {depts.map(d=><option key={d.id} value={d.id}>{d.dept_name}</option>)}
              </select>
            </FormField>
            <FormField label="Designation">
              <select className="form-select" value={form.designation_id} onChange={e=>f('designation_id',e.target.value)}>
                <option value="">Select Designation</option>
                {filteredDesigs.map(d=><option key={d.id} value={d.id}>{d.designation_name}</option>)}
              </select>
            </FormField>
            <FormField label="Role" required>
              <select className="form-select" value={form.role_id} onChange={e=>f('role_id',e.target.value)} required>
                <option value="">Select Role</option>
                {roles.filter(r=>r.role_code!=='citizen').map(r=><option key={r.id} value={r.id}>{r.role_name}</option>)}
              </select>
            </FormField>
            <FormField label="Login ID" required><input className="form-input" value={form.login_id} onChange={e=>f('login_id',e.target.value)} required disabled={!!editing} /></FormField>
            <FormField label={editing?'New Password (leave blank to keep)':'Password'} required={!editing}>
              <input type="password" className="form-input" value={form.password} onChange={e=>f('password',e.target.value)} required={!editing} placeholder={editing?'Leave blank to keep current':''} />
            </FormField>
            <FormField label="Email"><input type="email" className="form-input" value={form.email} onChange={e=>f('email',e.target.value)} /></FormField>
            <FormField label="Mobile"><input type="tel" className="form-input" value={form.mobile} onChange={e=>f('mobile',e.target.value)} /></FormField>
            <FormField label="Status">
              <select className="form-select" value={form.is_active} onChange={e=>f('is_active',e.target.value)}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </select>
            </FormField>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving?'Saving...':'Save Employee'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteModal} onClose={()=>setDeleteModal(null)} onConfirm={handleDelete}
        title="Deactivate Employee" message={`Deactivate "${deleteModal?.emp_name}"?`} confirmLabel="Deactivate" danger />
    </div>
  );
}
